// The per-trial rows behind the Performance tab's averages.
//
// This table is the reason the tab is trustworthy: a site averaging 2.25× on
// enrolled-per-site may have run one trial at 5.0× and another at 0.5×, and
// only the breakdown says which. Index columns lead for the same reason they
// lead the tab — they are the ones comparable between the rows.
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";

import { formatMetric, type MetricSummary } from "./PerformanceMetricFormat";

export type TrialMetricRow = Record<string, number | string | null> & {
  trial_nct_id: string;
};

const TRIAL_COLUMN_WIDTH = 130;
const METRIC_COLUMN_WIDTH = 120;
// Roughly ten rows before the body scrolls; 17 trials is the tenant maximum.
const BODY_MAX_HEIGHT = 380;

function metricColumn(
  metric: MetricSummary,
): ColumnsType<TrialMetricRow>[number] {
  return {
    title: metric.label,
    dataIndex: metric.key,
    key: metric.key,
    width: METRIC_COLUMN_WIDTH,
    align: "right",
    render: (value: number | string | null) => (
      <span className="text-[12px] tabular-nums text-[#2B4752]">
        {formatMetric(typeof value === "number" ? value : null, metric.unit)}
      </span>
    ),
  };
}

export default function PerformanceTrialTable({
  rows,
  indexMetrics,
  performanceMetrics,
  trialNames,
}: {
  rows: TrialMetricRow[];
  indexMetrics: MetricSummary[];
  performanceMetrics: MetricSummary[];
  /** NCT id → readable trial title, where the portfolio knows one. */
  trialNames: Record<string, string>;
}) {
  const columns: ColumnsType<TrialMetricRow> = [
    {
      title: "Trial",
      dataIndex: "trial_nct_id",
      key: "trial_nct_id",
      width: TRIAL_COLUMN_WIDTH,
      fixed: "left",
      render: (nct: string) => {
        const name = trialNames[nct];
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-[12px] font-medium text-[#1F2933]">
              {name ?? nct}
            </span>
            {/* the id is only worth a second line when it isn't the title */}
            {name && name !== nct && (
              <span className="text-[11px] text-[#8C8C8C]">{nct}</span>
            )}
          </div>
        );
      },
    },
    ...indexMetrics.map(metricColumn),
    ...performanceMetrics.map(metricColumn),
  ];

  return (
    <Table<TrialMetricRow>
      dataSource={rows}
      columns={columns}
      rowKey="trial_nct_id"
      size="small"
      pagination={false}
      scroll={{ x: "max-content", y: BODY_MAX_HEIGHT }}
    />
  );
}
